import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageMeta } from '@/components/PageMeta';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { 
  Search,
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
  Camera,
  TrendingUp,
  Globe,
  Lightbulb,
  Users,
  Tag,
} from 'lucide-react';

// Blog post interface
interface BlogPost {
  id: string | number;
  title: string;
  slug?: string;
  excerpt: string;
  image: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    role?: string;
  };
  date: string;
  readTime: string;
  featured?: boolean;
}

// Database blog post type
interface DatabaseBlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  cover_image_url: string | null;
  category: string;
  tags: string[] | null;
  status: string;
  author_name: string;
  author_avatar: string | null;
  published_at: string | null;
  created_at: string;
}

// Helper to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Helper to estimate read time
const estimateReadTime = (content: string) => {
  const wordsPerMinute = 200;
  const wordCount = content.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
};

// Convert database post to display format
const convertToDisplayPost = (post: DatabaseBlogPost): BlogPost => ({
  id: post.id,
  slug: post.slug,
  title: post.title,
  excerpt: post.excerpt || post.content.substring(0, 150) + '...',
  image: post.cover_image_url || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
  category: post.category,
  author: {
    name: post.author_name,
    avatar: post.author_avatar || 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop',
  },
  date: formatDate(post.published_at || post.created_at),
  readTime: estimateReadTime(post.content),
  featured: false,
});

// Category icons mapping
const categoryIcons: Record<string, React.ElementType> = {
  'All': Tag,
  'Tips & Tricks': Lightbulb,
  'Market Insights': TrendingUp,
  'Photo Enhancement': Camera,
  'Success Stories': Users,
  'Product Updates': Sparkles,
};

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [featuredPost, setFeaturedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch blog posts from database
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .eq('status', 'published')
          .order('published_at', { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const displayPosts = data.map(convertToDisplayPost);
          // First post is featured
          setFeaturedPost({ ...displayPosts[0], featured: true });
          // Rest are regular posts
          setPosts(displayPosts.slice(1));
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Compute categories dynamically from posts
  const allPosts = featuredPost ? [featuredPost, ...posts] : posts;
  const categories = [
    { name: 'All', count: allPosts.length },
    ...Object.entries(
      allPosts.reduce((acc, post) => {
        acc[post.category] = (acc[post.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    ).map(([name, count]) => ({ name, count }))
  ];

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      <PageMeta
        title="Blog – Real Estate Marketing Tips & AI Insights"
        description="Read the latest tips on AI photo enhancement, real estate marketing, and digital listing strategies from the ListBright team."
        breadcrumbs={[{ label: 'Blog', path: '/blog' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-background to-muted/30 py-24">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <Badge variant="secondary" className="px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              ListBright Blog
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold">
              Real Estate Insights & Resources
            </h1>
            <p className="text-xl text-muted-foreground">
              Tips, trends, and strategies to help you succeed in the property market.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search articles..."
                className="pl-10 pr-4 py-6"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="container max-w-7xl mx-auto px-4 -mt-8">
        {loading ? (
          <Card className="overflow-hidden border-border/50 shadow-lg">
            <div className="grid md:grid-cols-2">
              <Skeleton className="aspect-[4/3] md:h-[400px]" />
              <div className="p-8 space-y-4">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-20 w-full" />
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          </Card>
        ) : featuredPost ? (
        <Card className="overflow-hidden border-border/50 shadow-lg">
          <div className="grid md:grid-cols-2">
            <div className="aspect-[4/3] md:aspect-auto">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="p-8 flex flex-col justify-center">
              <Badge variant="secondary" className="w-fit mb-4">
                <Camera className="w-4 h-4 mr-1" />
                {featuredPost.category}
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">
                {featuredPost.title}
              </h2>
              <p className="text-muted-foreground mb-6">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={featuredPost.author.avatar}
                  alt={featuredPost.author.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-sm">{featuredPost.author.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {featuredPost.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {featuredPost.readTime}
                    </span>
                  </div>
                </div>
              </div>
              <Button asChild className="w-fit gap-2">
                <Link to={`/blog/${featuredPost.slug || featuredPost.id}`}>
                  Read Article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </Card>
        ) : (
          <Card className="overflow-hidden border-border/50 shadow-lg p-8 text-center">
            <p className="text-muted-foreground">No blog posts available yet. Check back soon!</p>
          </Card>
        )}
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Categories */}
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Categories</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {categories.map((category) => {
                      const Icon = categoryIcons[category.name] || Tag;
                      return (
                        <button
                          key={category.name}
                          onClick={() => setSelectedCategory(category.name)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedCategory === category.name
                              ? 'bg-primary text-primary-foreground'
                              : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {category.name}
                          </span>
                          <span className={`text-xs ${
                            selectedCategory === category.name
                              ? 'bg-primary-foreground/20 text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          } px-2 py-0.5 rounded-full`}>
                            {category.count}
                          </span>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Newsletter CTA */}
                <Card className="border-primary/20 bg-primary/5">
                  <CardHeader>
                    <CardTitle className="text-lg">Stay Updated</CardTitle>
                    <CardDescription>
                      Get the latest articles and tips delivered to your inbox.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Your email"
                    />
                    <Button className="w-full">Subscribe</Button>
                  </CardContent>
                </Card>
              </div>
            </aside>

            {/* Posts Grid */}
            <div className="lg:col-span-3">
              {loading ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <Card key={i} className="overflow-hidden border-border/50">
                      <Skeleton className="aspect-[16/10]" />
                      <CardHeader className="pb-2">
                        <Skeleton className="h-5 w-24" />
                        <Skeleton className="h-6 w-full" />
                      </CardHeader>
                      <CardContent className="pb-3">
                        <Skeleton className="h-12 w-full" />
                      </CardContent>
                      <CardFooter className="pt-0">
                        <div className="w-full flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Skeleton className="w-8 h-8 rounded-full" />
                            <div className="space-y-1">
                              <Skeleton className="h-3 w-20" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : filteredPosts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No articles found matching your criteria.</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {filteredPosts.map((post) => {
                    const CategoryIcon = categoryIcons[post.category] || Tag;
                    return (
                      <Link to={`/blog/${post.slug || post.id}`} key={post.id}>
                      <Card className="overflow-hidden border-border/40 card-premium h-full hover:shadow-lg transition-shadow">
                        <div className="aspect-[16/10]">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <CardHeader className="pb-2">
                          <Badge variant="secondary" className="w-fit text-xs">
                            <CategoryIcon className="w-3 h-3 mr-1" />
                            {post.category}
                          </Badge>
                          <CardTitle className="text-lg leading-snug line-clamp-2">
                            {post.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pb-3">
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                        </CardContent>
                        <CardFooter className="pt-0">
                          <div className="w-full flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <img
                                src={post.author.avatar}
                                alt={post.author.name}
                                className="w-8 h-8 rounded-full object-cover"
                                loading="lazy"
                              />
                              <div>
                                <p className="text-xs font-medium">{post.author.name}</p>
                                <p className="text-xs text-muted-foreground">{post.date}</p>
                              </div>
                            </div>
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime}
                            </span>
                          </div>
                        </CardFooter>
                      </Card>
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Load More */}
              {filteredPosts.length > 0 && (
                <div className="text-center mt-12">
                  <Button variant="outline" size="lg">
                    Load More Articles
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-card border-t border-border/40 section-padding">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <Globe className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="text-3xl font-display font-bold mb-4">
            Ready to Transform Your Listings?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join thousands of real estate professionals using ListBright worldwide.
          </p>
          <Link to="/">
            <Button variant="hero" size="lg">
              <Sparkles className="w-5 h-5 mr-2" />
              Create Your First Listing
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
